import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Send, CheckCircle2, AlertTriangle, Calendar, Building2, 
  FileText, Clock, Loader2, Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SendToTaskResponse = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    responder_name: '',
    responder_email: '',
    responder_company: '',
    response_type: '',
    response_text: ''
  });

  useEffect(() => {
    loadTokenInfo();
  }, [token]);

  const loadTokenInfo = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/correspondence/send-to-task/${token}`);
      setTokenInfo(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Invalid or expired link';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.responder_name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!formData.responder_email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    if (!formData.response_type) {
      toast.error('Please select a response type');
      return;
    }
    if (!formData.response_text.trim()) {
      toast.error('Please enter your response');
      return;
    }

    setSubmitting(true);
    
    try {
      const params = new URLSearchParams({
        responder_name: formData.responder_name,
        responder_email: formData.responder_email,
        response_type: formData.response_type,
        response_text: formData.response_text
      });
      if (formData.responder_company) {
        params.append('responder_company', formData.responder_company);
      }
      
      await axios.post(
        `${API_URL}/api/correspondence/send-to-task/${token}/respond?${params}`
      );
      
      setSubmitted(true);
      toast.success('Response submitted successfully');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to submit response';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-red-200">
          <CardContent className="pt-8 text-center">
            <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Link Invalid or Expired</h2>
            <p className="text-slate-600">{error}</p>
            <p className="text-sm text-slate-400 mt-4">
              Please contact the sender for a new response link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-green-200">
          <CardContent className="pt-8 text-center">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Response Submitted</h2>
            <p className="text-slate-600 mb-4">
              Thank you for your response regarding {tokenInfo.module_reference}.
            </p>
            <p className="text-sm text-slate-400">
              Your response has been recorded and will be processed by the project team.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Project Response Portal</h1>
          <p className="text-slate-500">
            Submit your response securely to the project team
          </p>
        </div>

        {/* Context Card */}
        <Card className="mb-6 border-blue-200 bg-blue-50/50" data-testid="send-to-task-context">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <FileText className="h-10 w-10 text-blue-600 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 mb-1">
                  Response Request for {tokenInfo.module_reference}
                </h3>
                <p className="text-slate-600 mb-3">
                  Project: <strong>{tokenInfo.project_name}</strong>
                </p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <Building2 className="h-3 w-3" />
                    {tokenInfo.module_type}
                  </Badge>
                  <Badge variant="outline" className="flex items-center gap-1 text-amber-700">
                    <Clock className="h-3 w-3" />
                    Expires: {new Date(tokenInfo.expires_at).toLocaleDateString()}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Response Form */}
        <Card data-testid="send-to-task-form">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-blue-600" />
              Submit Your Response
            </CardTitle>
            <CardDescription>
              Please provide the requested information below. All fields marked * are required.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Information */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                  <Shield className="h-4 w-4 text-slate-400" />
                  Your Information
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="responder_name" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Full Name *
                    </Label>
                    <Input
                      id="responder_name"
                      value={formData.responder_name}
                      onChange={(e) => setFormData({ ...formData, responder_name: e.target.value })}
                      placeholder="John Smith"
                      className="mt-1"
                      required
                      data-testid="responder-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="responder_email" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Email Address *
                    </Label>
                    <Input
                      id="responder_email"
                      type="email"
                      value={formData.responder_email}
                      onChange={(e) => setFormData({ ...formData, responder_email: e.target.value })}
                      placeholder="john@example.com"
                      className="mt-1"
                      required
                      data-testid="responder-email-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="responder_company" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Company / Organization
                  </Label>
                  <Input
                    id="responder_company"
                    value={formData.responder_company}
                    onChange={(e) => setFormData({ ...formData, responder_company: e.target.value })}
                    placeholder="ABC Engineering"
                    className="mt-1"
                    data-testid="responder-company-input"
                  />
                </div>
              </div>

              {/* Response Details */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-700">Response Details</h4>
                
                <div>
                  <Label htmlFor="response_type" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Response Type *
                  </Label>
                  <Select 
                    value={formData.response_type} 
                    onValueChange={(value) => setFormData({ ...formData, response_type: value })}
                  >
                    <SelectTrigger className="mt-1" data-testid="response-type-select">
                      <SelectValue placeholder="Select response type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="confirm">Confirm / Accept</SelectItem>
                      <SelectItem value="deny">Deny / Reject</SelectItem>
                      <SelectItem value="clarify">Request Clarification</SelectItem>
                      <SelectItem value="provide_info">Provide Information</SelectItem>
                      <SelectItem value="partial">Partial Response</SelectItem>
                      <SelectItem value="defer">Defer / Need More Time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="response_text" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Response Details *
                  </Label>
                  <Textarea
                    id="response_text"
                    value={formData.response_text}
                    onChange={(e) => setFormData({ ...formData, response_text: e.target.value })}
                    placeholder="Please provide your detailed response here..."
                    className="mt-1 min-h-[150px]"
                    required
                    data-testid="response-text-input"
                  />
                </div>
              </div>

              {/* Security Notice */}
              <Alert className="bg-slate-50 border-slate-200">
                <Shield className="h-4 w-4" />
                <AlertDescription className="text-xs text-slate-600">
                  Your response will be securely transmitted and recorded as part of the project correspondence. 
                  This submission is logged for audit purposes.
                </AlertDescription>
              </Alert>

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={submitting}
                data-testid="submit-response-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Response
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 mt-8">
          This is a secure response portal. Your submission is encrypted and recorded.
        </p>
      </div>
    </div>
  );
};

export default SendToTaskResponse;
